/**
 * Lesson Serialization and Deserialization
 * Requirements: 12.1, 12.2, 12.3, 12.4
 *
 * Provides functions to serialize EditorState to JSON and deserialize
 * with Zod validation for all blocks.
 */

import { z } from 'zod';
import type { ContentState, LessonMeta } from './editorStore';
import type {
  CategorizationBlock,
  CourseBlock,
  FreeformTextBlock,
  LikertScaleBlock,
  LocaleOption,
  SingleChoiceAnswerBlock,
  WorkflowState,
  WorkplaceColorSpectrumBlock,
} from '@course-authoring/shared-types';
import {
  CourseBlockSchema,
  type ValidationError,
  type ValidationResult,
} from '@course-authoring/shared-types';

/**
 * Serialized lesson format - JSON-compatible structure
 * Requirements: 12.1, 12.4
 */
export interface SerializedLesson {
  lessonMeta: LessonMeta;
  blocks: Record<string, CourseBlock>;
  blockOrder: string[];
}

/**
 * Zod schema for LessonMeta validation
 * Made flexible to handle backend responses that may not include all fields.
 */
const LessonMetaSchema = z.object({
  id: z.string().default(''),
  numericId: z.number().int().optional(),
  title: z.string().default('Untitled Lesson'),
  description: z.string().default(''),
  locale: z.string().optional(),
  defaultLocale: z.string().optional(),
  availableLocales: z.array(z.object({
    locale: z.string(),
    label: z.string(),
  })).optional(),
  fullyTranslatedLocales: z.array(z.string()).optional(),
  estimatedTimeMinutes: z.number().int().positive().optional(),
  themeColor: z.preprocess((val) => {
    const legacy: Record<string, string> = { blue: 'color1', magenta: 'color2', orange: 'color3', navy: 'color4' };
    return typeof val === 'string' && val in legacy ? legacy[val] : val;
  }, z.enum(['color1', 'color2', 'color3', 'color4'] as const)).optional(),
  workflowState: z.enum(['draft', 'published', 'archived'] as const)
    .optional()
    .default('draft'),
  currentVersionId: z.string().optional(),
  createdAt: z.string().default(''),
  updatedAt: z.string().default(''),
});

/**
 * Complete schema for serialized lesson validation
 * Accepts both frontend format and backend API format.
 */
const SerializedLessonSchema = z.object({
  id: z.string().optional(), // Backend includes id at top level
  lessonMeta: LessonMetaSchema,
  blocks: z.record(z.string(), CourseBlockSchema),
  blockOrder: z.array(z.string()),
});

/**
 * Lenient schema for loading lessons - accepts blocks without strict validation
 * This allows loading lessons with invalid blocks so users can fix them in the editor.
 */
const LenientBlockSchema = z.object({
  id: z.string(),
  type: z.string(),
}).passthrough(); // Accept any additional properties

const LenientSerializedLessonSchema = z.object({
  id: z.string().optional(),
  lessonMeta: LessonMetaSchema,
  blocks: z.record(z.string(), LenientBlockSchema).default({}),
  blockOrder: z.array(z.string()).default([]),
});

/**
 * Helper function to map Zod errors to ValidationError format
 */
function mapZodError(error: z.ZodError): ValidationError[] {
  return error.errors.map((err) => {
    let code: ValidationError['code'] = 'invalid_type';

    if (err.code === 'too_small') {
      code = err.minimum === 1 ? 'required' : 'too_short';
    } else if (err.code === 'invalid_string' && err.validation === 'url') {
      code = 'invalid_url';
    } else if (err.message.includes('caption')) {
      code = 'missing_caption';
    }

    return {
      field: err.path.join('.'),
      message: err.message,
      code,
    };
  });
}

/**
 * Strips backend row metadata and migrates the legacy field-level lock to the
 * block-level lock: any block with a non-empty `lockedFields` array becomes
 * `locked: true`. `lockedFields` is always removed; the normalized shape
 * persists with the next save, so stored payloads self-heal on demand.
 */
function normalizeBlocksForEditor(
  blocks: Record<string, Record<string, unknown>>
): Record<string, Record<string, unknown>> {
  return Object.fromEntries(
    Object.entries(blocks).map(([blockId, block]) => {
      const { lessonId, position, payload, lockedFields, ...rest } = block;
      const hasLegacyFieldLocks = Array.isArray(lockedFields) && lockedFields.length > 0;
      if (rest.locked === true || hasLegacyFieldLocks) {
        rest.locked = true;
      }
      return [blockId, rest];
    })
  );
}

/**
 * Serialize EditorState content to JSON string
 * Requirements: 12.1, 12.4
 *
 * Preserves all nested objects including styles, tracks, and content.
 *
 * @param content - The ContentState to serialize
 * @returns JSON string representation of the lesson
 */
export function serializeLesson(content: ContentState): string {
  const serialized: SerializedLesson = {
    lessonMeta: content.lessonMeta,
    blocks: content.blocks,
    blockOrder: content.blockOrder,
  };

  return JSON.stringify(serialized);
}

/**
 * Deserialize JSON string to ContentState with Zod validation
 * Requirements: 12.2, 12.3, 12.4
 *
 * Validates all blocks against their Zod schemas.
 * Returns ValidationResult with errors for invalid data.
 *
 * @param json - JSON string to deserialize
 * @returns ValidationResult containing either the parsed ContentState or validation errors
 */
export function deserializeLesson(json: string): ValidationResult<ContentState> {
  // First, try to parse the JSON
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return {
      success: false,
      errors: [{
        field: '',
        message: 'Invalid JSON format',
        code: 'invalid_type',
      }],
    };
  }

  return deserializeLessonFromObject(parsed);
}

/**
 * Question blocks persist a Question Bank reference, not a content snapshot:
 * resolved question fields are display-only in the store (materialized on
 * selection and re-resolved server-side on read/export) and are stripped on
 * save. Block-local fields survive: title/titleLevel, single-choice `gated`
 * (block mode), and Categorization's four feedback copies.
 *
 * Each list is keyed to its block interface (`keyof …Block`), so renaming a
 * resolved field in `blocks.ts` without updating this table is a compile error
 * rather than a silent leak of resolved content into the saved lesson.
 *
 * Exported because BOTH save paths must apply it: the whole-lesson PUT below,
 * and the per-block granular autosave in `lessonGranularPersistence.ts`. The
 * granular path used to send the store block verbatim, which persisted the
 * resolved Categorization `categories`/`items` — icons included — into the
 * block payload, and those icons carry an `imageLibraryItemId` that
 * `dtoToBlockFields` adds but the server's own resolver never emits. The
 * author-api image-reference sync then recorded that as block-owned image
 * usage the Question Bank could not clear.
 */
type ResolvedQuestionFieldMap = {
  'likert-scale': readonly (keyof LikertScaleBlock)[];
  'single-choice-answer': readonly (keyof SingleChoiceAnswerBlock)[];
  'workplace-color-spectrum': readonly (keyof WorkplaceColorSpectrumBlock)[];
  'categorization': readonly (keyof CategorizationBlock)[];
  // `purpose` is not a FreeformTextBlock field: the Question Bank modal's
  // scratch shape carries it and `dtoToBlockFields` seeds it on selection, so it
  // is named here as a plain string to keep it out of the saved block.
  'freeform-text': readonly (keyof FreeformTextBlock | 'purpose')[];
};

const RESOLVED_QUESTION_FIELDS: ResolvedQuestionFieldMap = {
  // NA-382: `descriptionBankValue` joins the strip list on every type, for the
  // same reason NA-313 added its explanation twin — resolution supplies it only
  // so the editor can detect divergence, and it is never authored content.
  // NA-384: `titleBankValue` gets the same treatment — unlike `title` itself
  // (which stays persisted unconditionally, see OVERRIDABLE_QUESTION_FIELDS
  // below not listing it), the bank-comparison value is resolution-only.
  'likert-scale': [
    'description', 'descriptionBankValue', 'titleBankValue',
    'scale', 'optionCount', 'healthyResponse',
  ],
  'single-choice-answer': [
    'description', 'descriptionBankValue', 'titleBankValue',
    'options', 'correctOptionId', 'healthyOptionId', 'correctExplanation',
    // NA-313: resolution supplies this purely so the editor can detect
    // divergence from the bank. Always stripped — it is never authored content.
    'correctExplanationBankValue',
  ],
  'workplace-color-spectrum': [
    'description', 'descriptionBankValue', 'titleBankValue',
    'colors', 'correctAnswer', 'correctAnswerExplanation',
    'correctAnswerExplanationBankValue',
  ],
  'categorization': [
    'description', 'descriptionBankValue', 'titleBankValue',
    'instructionText', 'categories', 'items',
  ],
  'freeform-text': [
    'description', 'descriptionBankValue', 'titleBankValue', 'purpose',
  ],
};

/**
 * NA-313 / NA-382: resolved fields a single block may take ownership of.
 *
 * Each field stays in `RESOLVED_QUESTION_FIELDS` above — so the `keyof …Block`
 * drift check still covers it — and is exempted from the strip at runtime only
 * when its marker says the block authored it. Keyed per block type, and a list
 * per type since Single Choice and Workplace Color Spectrum have two.
 */
type OverridableQuestionField<TBlock> = {
  field: keyof TBlock;
  marker: keyof TBlock;
};

type OverridableQuestionFieldMap = {
  'likert-scale': readonly OverridableQuestionField<LikertScaleBlock>[];
  'single-choice-answer': readonly OverridableQuestionField<SingleChoiceAnswerBlock>[];
  'workplace-color-spectrum': readonly OverridableQuestionField<WorkplaceColorSpectrumBlock>[];
  'categorization': readonly OverridableQuestionField<CategorizationBlock>[];
  'freeform-text': readonly OverridableQuestionField<FreeformTextBlock>[];
};

// NA-382: every bank-backed type names its question text the same, so the entry
// is identical across all four — no per-type naming to get wrong.
const QUESTION_TEXT_OVERRIDE = {
  field: 'description',
  marker: 'descriptionOverridden',
} as const;

const OVERRIDABLE_QUESTION_FIELDS: OverridableQuestionFieldMap = {
  'likert-scale': [QUESTION_TEXT_OVERRIDE],
  'single-choice-answer': [
    QUESTION_TEXT_OVERRIDE,
    {
      field: 'correctExplanation',
      marker: 'correctExplanationOverridden',
    },
  ],
  'workplace-color-spectrum': [
    QUESTION_TEXT_OVERRIDE,
    {
      field: 'correctAnswerExplanation',
      marker: 'correctAnswerExplanationOverridden',
    },
  ],
  'categorization': [QUESTION_TEXT_OVERRIDE],
  'freeform-text': [QUESTION_TEXT_OVERRIDE],
};

export function stripResolvedQuestionFields(block: CourseBlock): CourseBlock {
  if (
    !(block.type in RESOLVED_QUESTION_FIELDS) ||
    !('questionDefinitionId' in block) ||
    block.questionDefinitionId === undefined
  ) {
    return block;
  }
  const resolvedFields = RESOLVED_QUESTION_FIELDS[block.type as keyof ResolvedQuestionFieldMap];
  const copy: Record<string, unknown> = { ...block };

  const overridables = (OVERRIDABLE_QUESTION_FIELDS[block.type as keyof OverridableQuestionFieldMap] ?? []) as
    readonly { field: string; marker: string }[];

  const ownedFields = new Set<string>();
  for (const overridable of overridables) {
    if (copy[overridable.marker] === true) {
      ownedFields.add(overridable.field);
      continue;
    }
    // Keep "absent means inheriting" the single representation: a block that is
    // not overridden never persists a marker, so a stale falsy one cannot linger
    // and later be mistaken for a half-written override.
    delete copy[overridable.marker];
  }

  for (const field of resolvedFields) {
    // The one exception to "question blocks persist a reference, not a
    // snapshot": a field this block has taken ownership of is authored content
    // and must survive the save.
    if (ownedFields.has(field)) continue;
    delete copy[field];
  }

  return copy as unknown as CourseBlock;
}

/**
 * Serialize ContentState to a plain object (not stringified)
 * Useful for direct object manipulation or storage
 *
 * @param content - The ContentState to serialize
 * @returns SerializedLesson object
 */
export function serializeLessonToObject(content: ContentState): SerializedLesson {
  const blocks: Record<string, CourseBlock> = {};
  for (const [blockId, block] of Object.entries(content.blocks)) {
    blocks[blockId] = stripResolvedQuestionFields(block);
  }
  return {
    lessonMeta: { ...content.lessonMeta },
    blocks,
    blockOrder: [...content.blockOrder],
  };
}

/**
 * Deserialize a plain object to ContentState with Zod validation
 * Requirements: 12.2, 12.3
 *
 * @param data - Object to deserialize
 * @returns ValidationResult containing either the parsed ContentState or validation errors
 */
export function deserializeLessonFromObject(data: unknown): ValidationResult<ContentState> {
  const result = SerializedLessonSchema.safeParse(data);

  if (!result.success) {
    return {
      success: false,
      errors: mapZodError(result.error),
    };
  }

  const validatedData = result.data;

  // Use top-level id if lessonMeta.id is missing (backend format)
  const lessonId = validatedData.lessonMeta.id || validatedData.id || '';

  return {
    success: true,
    data: {
      lessonMeta: {
        ...validatedData.lessonMeta,
        id: lessonId,
        workflowState: (validatedData.lessonMeta.workflowState ?? 'draft') as WorkflowState,
        availableLocales: validatedData.lessonMeta.availableLocales as LocaleOption[] | undefined,
        fullyTranslatedLocales: validatedData.lessonMeta.fullyTranslatedLocales,
      },
      blocks: normalizeBlocksForEditor(
        validatedData.blocks as unknown as Record<string, Record<string, unknown>>
      ) as unknown as Record<string, CourseBlock>,
      blockOrder: validatedData.blockOrder,
    },
  };
}

/**
 * Lenient deserialize - loads lesson data without strict block validation.
 *
 * @param data - Object to deserialize
 * @returns ValidationResult containing either the parsed ContentState or structural errors only
 */
export function deserializeLessonFromObjectLenient(data: unknown): ValidationResult<ContentState> {
  const lessonResult = LenientSerializedLessonSchema.safeParse(data);
  if (lessonResult.success) {
    const validatedData = lessonResult.data;
    const lessonId = validatedData.lessonMeta.id || validatedData.id || '';

    return {
      success: true,
      data: {
        lessonMeta: {
          ...validatedData.lessonMeta,
          id: lessonId,
          workflowState: (validatedData.lessonMeta.workflowState ?? 'draft') as WorkflowState,
          availableLocales: validatedData.lessonMeta.availableLocales as LocaleOption[] | undefined,
          fullyTranslatedLocales: validatedData.lessonMeta.fullyTranslatedLocales,
        },
        blocks: normalizeBlocksForEditor(
          validatedData.blocks as unknown as Record<string, Record<string, unknown>>
        ) as unknown as Record<string, CourseBlock>,
        blockOrder: validatedData.blockOrder,
      },
    };
  }

  return {
    success: false,
    errors: mapZodError(lessonResult.error),
  };
}
